// @flow
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Modal from "components/Modal";
import ButtonToolbar from "react-bootstrap/lib/ButtonToolbar";
import Button from "react-bootstrap/lib/Button";
import SplitButton from "react-bootstrap/lib/SplitButton";
import MenuItem from "react-bootstrap/lib/MenuItem";
import Icons from "@hs/icons";
import FilterValue from "./FilterValue";
import {
    type DocListFilterRequestDTO,
    DocListFilterOperators,
    DocListFilterType,
} from "data/types";
import { type docsResultState } from "data/reducers/types";
import loc from "i18next";
import styles from "./DocListFilterModal.module.css";
import { GetDefaults } from "./Utils";
import { produce } from "immer";
import Formsy, { addValidationRule } from "formsy-react";
import { getClassNames, isNotInvalid } from "data/utils";
import { useUpdateEffect } from "hooks";

type Props = {
    /** whether to show modal */
    show: boolean,
    /** which filter to focus on */
    focusOnFilterName: ?string,
    /** modal dialog title */
    title: string,
    /** current result */
    result: docsResultState,
    /** callback when user closes modal */
    onClose: () => void,
    /** callback when user wants to set filters
     * @param {Array<DocListFilterRequestDTO>} filters all filters values
     */
    onFilter: (filters: Array<DocListFilterRequestDTO>) => void,
    /** current filters definitions */
    filters: ?Array<DocListFilterRequestDTO>,
    /** whether the DocListFilterModal is used for a FlatAU or not */
    forFlatAU: ?boolean,
};

const DocListFilterModal = (props: Props) => {
    const {
        show,
        onClose,
        result,
        title,
        filters: initialFilters,
        onFilter,
        focusOnFilterName,
        forFlatAU,
    } = props;
    const [filters, setFilters] = useState<Array<DocListFilterRequestDTO>>([]);
    const [canSubmit, setCanSubmit] = useState(true);

    const _setupValidation = useCallback(() => {
        addValidationRule("isNotInvalid", isNotInvalid);
    }, []);

    const hasFilters = filters.length > 0;
    useEffect(() => {
        // if we already have filters, don't do anything
        /* istanbul ignore next */
        if (hasFilters) return;

        if (!initialFilters || initialFilters.length === 0) {
            if (result.itemUri != null) {
                setFilters(
                    GetDefaults(result.itemUri, props.formatId, result.cols)
                        .filters || /* istanbul ignore next */ []
                );
            } else {
                setFilters([]);
            }
        } else {
            setFilters(initialFilters);
        }
        _setupValidation();
    }, [initialFilters, result, props.formatId, _setupValidation, hasFilters]);

    const _enableSubmit = useCallback(() => setCanSubmit(true), []);
    const _disableSubmit = useCallback(() => setCanSubmit(false), []);

    const _convertFilters = useCallback(
        (filters: Array<DocListFilterRequestDTO>) =>
            filters.map((filter) => {
                const col = result?.cols?.find((c) => c.name === filter.name);
                return {
                    ...filter,
                    values:
                        col?.extra?.format === DocListFilterType.PrincipalLookup
                            ? filter.values.map((v) => v.displayName || "")
                            : filter.values,
                };
            }),
        [result]
    );

    const _onFilter = useCallback(() => {
        onFilter(_convertFilters(filters));
    }, [filters, onFilter, _convertFilters]);

    const _onSubmit = useCallback(() => {
        _onFilter();
    }, [_onFilter]);

    const _onOperatorChange = useCallback(
        (e: SyntheticInputEvent<*>, index: number): void => {
            const filter = filters[index];
            setFilters(
                produce(filters, (draft) => {
                    draft[index].operator = e.currentTarget.value;
                    if (
                        filter.operator ===
                            DocListFilterOperators.DateRelative ||
                        draft[index].operator ===
                            DocListFilterOperators.DateRelative
                    ) {
                        draft[index].values = [""];
                    }
                    if (
                        filter.operator === DocListFilterOperators.Between &&
                        draft[index].operator !== DocListFilterOperators.Between
                    ) {
                        draft[index].values.splice(1, 1);
                    }
                })
            );
        },
        [filters]
    );

    const _onValueChange = useCallback((value: string, index: number) => {
        setFilters((prevFilters) =>
            produce(prevFilters, (draft) => {
                draft[index].values = Array.isArray(value) ? value : [value];
            })
        );
    }, []);

    const _resetSelect = useCallback(
        (eventKey: "all" | "values") => {
            /* istanbul ignore else */
            if (eventKey === "all") {
                const { result, formatId } = props;
                if (result.itemUri != null) {
                    setFilters(
                        GetDefaults(result.itemUri, formatId, result.cols)
                            .filters
                    );
                } else {
                    setFilters([]);
                }
            } else if (eventKey === "values") {
                /* istanbul ignore next */
                setFilters(
                    filters.map((filter) =>
                        produce(filter, (draft) => {
                            draft.values = [""];
                        })
                    )
                );
            }
        },
        [filters, props]
    );

    const _resetValues = useCallback(
        () => _resetSelect("values"),
        [_resetSelect]
    );

    // reset filters when initialFilters change
    useUpdateEffect(() => {
        if (initialFilters == null) {
            _resetSelect("all");
        } else {
            setFilters(initialFilters);
        }
    }, [initialFilters]);

    const _onKeyPress = useCallback(
        (e: SyntheticKeyboardEvent<*>) => {
            if (e.which === 13) {
                e.preventDefault();
                _onFilter();
            }
        },
        [_onFilter]
    );

    const _getFocusColumnIndex = useCallback(() => {
        if (focusOnFilterName == null) {
            const cols = result.cols || [];
            return filters.findIndex((filter) => {
                const col = cols.find((c) => c.name === filter.name);
                return col != null && col.canSort;
            });
        }
        const i = filters.findIndex(
            (filter) => filter.name === focusOnFilterName
        );
        /* istanbul ignore next */
        return i === -1 ? null : i;
    }, [filters, focusOnFilterName, result.cols]);

    const cols = useMemo(() => result.cols || [], [result.cols]);
    const focusColumnIndex = _getFocusColumnIndex();

    const containsRequired = useMemo(
        () => forFlatAU && cols.some((col) => col.isRequired),
        [cols]
    );

    return (
        <Modal
            enforceFocus={false}
            dialogClassName={styles.modal}
            show={show}
            onHide={onClose}
            onKeyPress={_onKeyPress}
            scroll
            footerSize={55}
            closable={!containsRequired}
        >
            <Formsy
                onValidSubmit={_onSubmit}
                onValid={_enableSubmit}
                onInvalid={_disableSubmit}
                autoComplete="off"
                className={getClassNames("form-inline", "ip-form")}
            >
                <Modal.Header closeButton={!containsRequired}>
                    <Modal.Title>
                        <Icons.Library name="filter" />
                        {" " +
                            loc.t("folder:action_filter.title") +
                            " " +
                            title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {filters.map((filter, index) => {
                        const col = cols.find((c) => c.name === filter.name);
                        if (col == null || !col.canSort) return null;
                        return (
                            <FilterValue
                                key={col.name}
                                index={index}
                                autoFocus={focusColumnIndex === index}
                                value={filter.values}
                                type={col.type}
                                operator={filter.operator}
                                onChange={_onValueChange}
                                id={col.name}
                                onOperatorChange={(e) =>
                                    _onOperatorChange(e, index)
                                }
                                filter={filter}
                                col={col}
                                name={col.name}
                                header={col.caption}
                                showLabel={true}
                                extra={col.extra}
                                formatId={props.formatId}
                                isRequired={forFlatAU && col.isRequired}
                            />
                        );
                    })}
                </Modal.Body>
                <Modal.Footer>
                    <ButtonToolbar
                        className="pull-left"
                        onClick={
                            /* istanbul ignore next */ (e) =>
                                e.stopPropagation()
                        }
                    >
                        <SplitButton
                            id="reset-button"
                            dropup
                            bsStyle="default"
                            title={loc.t("folder:action_filter.reset")}
                            aria-label="reset"
                            onClick={_resetValues}
                            onSelect={_resetSelect}
                        >
                            <MenuItem eventKey="all" id="resetall-button">
                                {loc.t("folder:action_filter.reset_all")}
                            </MenuItem>
                        </SplitButton>
                    </ButtonToolbar>
                    <ButtonToolbar className="pull-right">
                        <Button
                            id="filter-button"
                            aria-label="filter"
                            type="submit"
                            bsStyle="primary"
                            disabled={!canSubmit}
                            style={{ padding: "6px 30px" }}
                        >
                            {loc.t("folder:action_filter.filter")}
                        </Button>
                    </ButtonToolbar>
                </Modal.Footer>
            </Formsy>
        </Modal>
    );
};

export default DocListFilterModal;
